"use strict";
/**
 * Aggregator profiles: what a tenant can use, by the aggregator it writes
 * business through.
 *
 * A tenant's aggregator is an EXPLICIT setting (`companies/{id}.aggregator`),
 * chosen by the founder in the admin portal; it is never inferred from data.
 * The profile says which CRM that aggregator's brokers use and which
 * aggregator-specific features exist for them. A feature is available to a
 * tenant only when its profile lists it AND the tenant's own featureFlag for it
 * is switched on (the profile says "can", the flag says "is rolled out").
 *
 * Not to be confused with aggregatorRegistry.ts, which detects commission
 * statement FILES by entity code.
 *
 * Adding an aggregator: one entry below. Adding a feature: add its key to
 * AGGREGATOR_FEATURES, list it on the profiles that support it, and gate the
 * page/callable with tenantHasAggregatorFeature().
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.AGGREGATOR_FEATURE_FLAGS = exports.AGGREGATOR_KEYS = exports.AGGREGATOR_PROFILES = exports.AGGREGATOR_FEATURES = void 0;
exports.parseAggregatorKey = parseAggregatorKey;
exports.aggregatorProfile = aggregatorProfile;
exports.tenantHasAggregatorFeature = tenantHasAggregatorFeature;
/**
 * Features that depend on the aggregator (its CRM or statement format).
 * - linked_deals: the "Linked deals" page. Review and confirm deal -> loan
 *   links between the aggregator CRM and the commission statements, including
 *   the accuracy check that gates writing automatic links back to the CRM.
 *   Needs a CRM with a deal API VALUTTEN reads (MyCRM).
 */
exports.AGGREGATOR_FEATURES = ['linked_deals'];
exports.AGGREGATOR_PROFILES = {
    LMG: { key: 'LMG', name: 'Loan Market Group', crm: 'mycrm', features: ['linked_deals'] },
    AFG: { key: 'AFG', name: 'AFG', crm: null, features: [] },
    CONNECTIVE: { key: 'CONNECTIVE', name: 'Connective', crm: null, features: [] },
    FINSURE: { key: 'FINSURE', name: 'Finsure', crm: null, features: [] },
    CHOICE: { key: 'CHOICE', name: 'Choice Aggregation Services', crm: null, features: [] },
    VISION: { key: 'VISION', name: 'Vision Abacus', crm: null, features: [] },
    ASTUTE: { key: 'ASTUTE', name: 'Astute Financial', crm: null, features: [] },
    OTHER: { key: 'OTHER', name: 'Other / independent', crm: null, features: [] },
};
exports.AGGREGATOR_KEYS = Object.keys(exports.AGGREGATOR_PROFILES);
/** Parse `companies/{id}.aggregator`. Anything not an exact known key = null (no aggregator features). */
function parseAggregatorKey(raw) {
    return typeof raw === 'string' && Object.prototype.hasOwnProperty.call(exports.AGGREGATOR_PROFILES, raw) ? raw : null;
}
function aggregatorProfile(raw) {
    const key = parseAggregatorKey(raw);
    return key ? exports.AGGREGATOR_PROFILES[key] : null;
}
/**
 * Tenant flag for each aggregator feature: `companies/{id}.featureFlags.<key>`.
 * Only the exact string 'enabled' switches it on (fail closed).
 */
exports.AGGREGATOR_FEATURE_FLAGS = {
    linked_deals: 'linked_deals',
};
/**
 * Whether a tenant can use an aggregator feature: its aggregator's profile
 * lists the feature AND its own featureFlag is 'enabled'. Pass the company
 * document's data (or the subset with `aggregator` and `featureFlags`).
 */
function tenantHasAggregatorFeature(company, feature) {
    const profile = aggregatorProfile(company?.aggregator);
    if (!profile || !profile.features.includes(feature))
        return false;
    return company?.featureFlags?.[exports.AGGREGATOR_FEATURE_FLAGS[feature]] === 'enabled';
}
